import { postJson } from './httpClient';

/**
 * Customer as returned by auth endpoints.
 * @typedef {Object} Customer
 * @property {string} id
 * @property {string} [name]
 * @property {string} [email]
 * @property {string} [phone]
 */

/**
 * @typedef {Object} AuthInitiateResponse
 * @property {boolean} success
 * @property {string} message
 * @property {{ otp_required?: string, customer?: Customer }} data
 */

/**
 * @typedef {Object} VerifyResponse
 * @property {boolean} success
 * @property {string} message
 * @property {{ customer?: Customer, token?: string, token_type?: string }} data
 */

export async function signupCustomer({ name, email, phone }, fetchInit) {
  /** @type {AuthInitiateResponse} */
  const json = await postJson('/signup', {
    body: {
      name: String(name || '').trim(),
      email: String(email || '').trim(),
      ...(String(phone || '').trim() ? { phone: String(phone).trim() } : {}),
    },
    ...fetchInit,
  });

  if (!json?.success) throw new Error(json?.message || 'Signup failed');
  return json;
}

export async function loginCustomer({ email, phone }, fetchInit) {
  /** @type {AuthInitiateResponse} */
  const json = await postJson('/login', {
    body: {
      email: String(email || '').trim(),
      ...(String(phone || '').trim() ? { phone: String(phone).trim() } : {}),
    },
    ...fetchInit,
  });

  if (!json?.success) throw new Error(json?.message || 'Login failed');
  return json;
}

export async function verifyCustomer({ email, otp }, fetchInit) {
  /** @type {VerifyResponse} */
  const json = await postJson('/verify', {
    body: {
      email: String(email || '').trim(),
      otp: String(otp || '').trim(),
    },
    ...fetchInit,
  });

  if (!json?.success) throw new Error(json?.message || 'Verify failed');
  if (!json?.data?.token) throw new Error('Verify succeeded but token missing');
  return json;
}

/**
 * Logs out the current customer.
 * Endpoint: POST /logout (expects Bearer token).
 *
 * If your backend uses a different path, change it here only.
 */
export async function logoutCustomer(token, fetchInit) {
  if (!String(token || '').trim()) return { success: true };
  const json = await postJson('/logout', {
    token: String(token).trim(),
    ...fetchInit,
  });
  if (json && json.success === false) {
    throw new Error(json.message || 'Logout failed');
  }
  return json;
}

